import { optimizedRetryOption } from "o4a-react";
import * as React from "react";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import { ttlOneMinCaching } from "../../constants/uiConstants";
import { parseId } from "../../helpers/idHelper";
import { OciResourceType } from "../../helpers/resourceHelper";
import { useQueryCall } from "../../hooks/useQueryCall";
import { AzureSubscriptionSummaryExt } from "../../hooks/useSubscriptions";
import { getOciRegion } from "../../utils";
import { OciLink, OciLinkProps } from "./OciLink";

export interface OciExaCdbLinkProps extends Pick<OciLinkProps, "location" | "analytics"> {
  id: string;
  subscriptions: AzureSubscriptionSummaryExt[];
}

export const OciExaCdbLink = ({ id, location, subscriptions, analytics }: OciExaCdbLinkProps): JSX.Element => {
  const idComps = parseId(id);
  const resGroup = decodeURIComponent(idComps?.resourceGroup || "");
  const resourceName = decodeURIComponent(idComps?.resourceName || "");
  const subscription = subscriptions?.find(item => item.id === idComps?.subscriptionId);

  const { response: responseCdb, error: errorCdb } = useQueryCall({
    wait: !id,
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.getDatabase,
    options: {
      args: {
        subscriptionId: idComps?.subscriptionId || "",
        resourceGroupName: resGroup,
        apiVersion: MultiCloudDatabaseApiVersion,
        databaseName: resourceName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
  });

  const cdb = responseCdb?.data;

  const vmClusterIdComps = parseId(cdb?.vmClusterId);
  const vmClusterResGroup = decodeURIComponent(vmClusterIdComps?.resourceGroup || "");
  const vmClusterResourceName = decodeURIComponent(vmClusterIdComps?.resourceName || "");

  const { response: responseVmCluster, error: errorVmCluster } = useQueryCall({
    wait: !cdb?.vmClusterId,
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.getCloudVmCluster,
    options: {
      args: {
        subscriptionId: vmClusterIdComps?.subscriptionId || "",
        resourceGroupName: vmClusterResGroup,
        apiVersion: MultiCloudDatabaseApiVersion,
        cloudVmClusterName: vmClusterResourceName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
  });

  const vmCluster = responseVmCluster?.data;

  const dbHomeIdComps = parseId(cdb?.dbHomeId);
  const dbHomeResGroup = decodeURIComponent(dbHomeIdComps?.resourceGroup || "");
  const dbHomeResourceName = decodeURIComponent(dbHomeIdComps?.resourceName || "");

  const { response: responseDbHome, error: errorDbHome } = useQueryCall({
    wait: !cdb?.dbHomeId,
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.getDbHome,
    options: {
      args: {
        subscriptionId: dbHomeIdComps?.subscriptionId || "",
        resourceGroupName: dbHomeResGroup,
        apiVersion: MultiCloudDatabaseApiVersion,
        dbHomeName: dbHomeResourceName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
  });

  const dbHome = responseDbHome?.data;

  const ociResourcePath = `/dbaas/cloudVmClusters/${vmCluster?.ocid}/databases/${dbHome?.ocid}/${cdb?.ocid}`;

  return (
    <OciLink
      ociResourceType={OciResourceType.EXA_CDB}
      ociResourcePath={!errorCdb && !errorDbHome && !errorVmCluster && vmCluster?.ocid && dbHome?.ocid && cdb?.ocid
        ? ociResourcePath : undefined}
      ociResourceName={cdb?.dbName}
      location={location}
      subscription={subscription}
      analytics={analytics}
    />
  );
};
